import { getStyleByClass, removeHTMLTags, transpose } from "./helpers/main";
import { IXlsxExportConfig, ICsvExportConfig, IGrid } from "./types";
import { CsvDriver } from "@dhx/ts-data";
import { downloadFile } from "@dhx/ts-common/core";

function fillArray(arr: any[], value: any): any[] {
	for (let i = 0;i < arr.length;i++) {
		arr[i] = value;
	}
	return arr;
}
export class Exporter {
	constructor(private _view: IGrid) { }
	xlsx(config?: IXlsxExportConfig) {
		return this._export(config);
	}
	csv(config: ICsvExportConfig = {}) {
		config = { ...{ asFile: true, rowDelimiter: "\n", columnDelimiter: ",", skipHeader: 0 }, ...config };
		let csv: string;
		if ("getRoot" in this._view.data && config.flat) {
			csv = this.getFlatCSV(config);
		} else {
			csv = this._getCSV(config);
		}
		const name = config.name || "grid_export";

		if (config.asFile) {
			downloadFile(csv, `${name}.csv`, "text/csv");
		}
		return csv;
	}
	private _export(config: IXlsxExportConfig = {}) {
		const configCols = this._view.config.columns;
		const rowsIndexMap = {};
		const headers = transpose(this._view.config.columns.map(col =>
			col.header.map(level => level.text || " ")
		));

		const columns = [];
		const uniqStyles = {
			default: {
				color: "#000000",
				background: "#FFFFFF",
				fontSize: 14
			}
		};
		const cells = [];
		const columnsIndexMap = {};

		const data = this._view.data.serialize().map((row, i) => {
			rowsIndexMap[row.id] = i;
			const rowData = configCols.map((col, k) => {
				columnsIndexMap[col.id] = k;
				return removeHTMLTags(row[col.id]);
			});
			return rowData;
		});

		for (const col of configCols) {
			columns.push({ width: col.width });
			for (const key in col.$cellCss) {
				const colStyle = col.$cellCss[key];
				const colStyleHash = colStyle
					.split("")
					.reduce((h, letter) => {
						// tslint:disable-next-line:no-bitwise
						const hh = ((h << 5) - h) + letter.charCodeAt(0); return Math.abs(hh & hh);
					}, 0).toString();

				if (!uniqStyles[colStyleHash]) {
					const cont = document.body as HTMLElement;
					const css = getStyleByClass(colStyle, cont, "dhx_grid-row", uniqStyles.default);
					if (css) {
						uniqStyles[colStyleHash] = css;
					}
				}
				if (uniqStyles[colStyleHash]) {
					cells.push([rowsIndexMap[key], col.id, colStyleHash]);
				}
			}
		}

		const exportData = {
			name: (config.name || "data"),
			columns,
			header: headers,
			data,
			styles: {
				cells,
				css: uniqStyles
			}
		};

		if (config.url) {
			const form = document.createElement("form");
			form.setAttribute("target", "_blank");
			form.setAttribute("action", config.url);
			form.setAttribute("method", "POST");
			form.style.visibility = "hidden";

			const input = document.createElement("textarea");
			input.setAttribute("name", "data");
			input.value = JSON.stringify(exportData);

			form.appendChild(input);
			document.body.appendChild(form);
			form.submit();

			setTimeout(function() {
				form.parentNode.removeChild(form);
			}, 100);
		}

		return exportData;
	}

	private getFlatCSV(config: ICsvExportConfig) {
		const treeData: any = this._view.data;
		const root = treeData.getRoot();
		const firstCol = this._view.config.columns[0];


		const maxLevel = treeData.getMaxLevel();

		const getParentsChain = (item: any, data: any): string[] => {
			const parents = [];
			for (let i = 0;i <= maxLevel;i++) {
				if (item && item[firstCol.id]) {
					parents[item.$level] = item[firstCol.id];
					const parent = data.getParent(item.id, true);
					if (parent && parent.id) {
						item = parent;
					} else {
						item = null;
					}
				} else {
					parents[i] = "";
				}
			}

			return parents;
		};

		let total = "";
		treeData.eachChild(root, (item) => {
			const parents = getParentsChain(item, treeData).join(config.columnDelimiter);

			total += parents + Object.keys(item).reduce((values, key, i) => {
				if (key !== "id" && key !== "parent" && key[0] !== "$" && i !== 0) {
					return values + config.columnDelimiter + (item[key] === null ? "" : item[key]);
				}
				return values;
			}, "");

			total += config.rowDelimiter;
		});

		const exportData = this._export(config);

		// [dirty]
		const emptyHeaders = fillArray(new Array(maxLevel + 1), "");

		const headers = exportData.header.map(header => {
			header.splice(0, 1, ...emptyHeaders);
			return header;
		});

		const head = new CsvDriver(config).serialize(headers, true) + config.rowDelimiter;
		return head + total;
	}
	private _getCSV(config: ICsvExportConfig) {
		const exportData = this._export(config);
		const headers = exportData.header;

		const driver = new CsvDriver(config);

		const head = driver.serialize(headers, true);
		const readyData = driver.serialize(exportData.data, true);

		return head + readyData;
	}
}